import { ApiClientError } from '@/lib/api/client'
import type { AuthGuard } from '@/lib/jwt'
import type { TotpStepUpStatus } from '@/types/two-factor'

export const TWO_FACTOR_ENROLLMENT_REQUIRED = 'two_factor_enrollment_required'
export const TWO_FACTOR_STEP_UP_REQUIRED = 'two_factor_step_up_required'

export const TWO_FACTOR_STEP_UP_REQUIRED_MESSAGE =
  'Confirme o código do autenticador para continuar.'
export const TWO_FACTOR_ENROLLMENT_REQUIRED_MESSAGE =
  'Configure a autenticação em duas etapas para gerenciar campanhas.'

export type TotpStepUpCopy = {
  enrollmentHint: string
  subtitle: string
}

export type TotpStepUpPromptReason = 'code' | 'enrollment'

export const CAMPAIGN_TOTP_STEP_UP_COPY: TotpStepUpCopy = {
  subtitle: 'Para criar ou editar campanhas, confirme o código do autenticador desta conta.',
  enrollmentHint:
    'Esta conta ainda não tem autenticação em duas etapas. Configure um autenticador para gerenciar campanhas.',
}

export const ADMIN_SENSITIVE_TOTP_STEP_UP_COPY: TotpStepUpCopy = {
  subtitle: 'Para acessar esta área, confirme o código do autenticador desta conta.',
  enrollmentHint:
    'Esta conta ainda não tem autenticação em duas etapas. Configure um autenticador para acessar esta área.',
}

export function canAccessAdminSensitiveArea(guard: AuthGuard | null | undefined): boolean {
  return guard === 'admin'
}

export function canManageInstitutionCampaigns(
  guard: AuthGuard | null | undefined,
  isCollaborator: boolean
): boolean {
  if (isCollaborator) return false
  return guard === 'admin' || guard === 'institution'
}

export function isTotpStepUpStillElevated(status: TotpStepUpStatus | null | undefined): boolean {
  if (!status?.elevated) {
    return false
  }

  if (!status.expires_at) {
    return false
  }

  const expiresAtMs = Date.parse(status.expires_at)
  return Number.isFinite(expiresAtMs) && expiresAtMs > Date.now()
}

function matchesKnownTwoFactorMessage(message: string): boolean {
  return (
    message.includes(TWO_FACTOR_STEP_UP_REQUIRED_MESSAGE) ||
    message.includes(TWO_FACTOR_ENROLLMENT_REQUIRED_MESSAGE)
  )
}

export function isCampaignTotpStepUpError(error: unknown): error is ApiClientError {
  if (!(error instanceof ApiClientError) || error.status !== 403) {
    return false
  }

  return (
    error.code === TWO_FACTOR_ENROLLMENT_REQUIRED ||
    error.code === TWO_FACTOR_STEP_UP_REQUIRED ||
    matchesKnownTwoFactorMessage(error.message)
  )
}

export function getTotpStepUpPromptReason(error: ApiClientError): TotpStepUpPromptReason {
  if (error.code === TWO_FACTOR_ENROLLMENT_REQUIRED) {
    return 'enrollment'
  }

  if (error.code === TWO_FACTOR_STEP_UP_REQUIRED) {
    return 'code'
  }

  if (error.message.includes(TWO_FACTOR_ENROLLMENT_REQUIRED_MESSAGE)) {
    return 'enrollment'
  }

  return 'code'
}
